/**
 * Install a one-time global fetch wrapper that stamps every call to OUR backend
 * (`/api/*` and `/sb-fn/*`) with the correlation headers `X-Request-Id` (fresh per call)
 * and `X-Session-Id` (stable per tab).
 *
 * Why a global wrapper instead of editing each call site: AdCanvas has no central fetch
 * client — requests are spread across utils/api.js, supermetrics.js, contexts, etc. One
 * wrapper guarantees coverage with the smallest possible change and zero risk of a missed
 * call site. Third-party calls (PostHog, Sentry, Supabase auth/db) are deliberately left
 * untouched so we never add headers that would trip their CORS preflight.
 */
import { getSessionId, newRequestId } from './requestId';

let installed = false;

/** True for same-origin requests whose path targets our serverless or edge backend. */
function isBackendCall(url: string): boolean {
  try {
    const u = new URL(url, window.location.origin);
    if (u.origin !== window.location.origin) return false;
    return u.pathname.startsWith('/api/') || u.pathname.startsWith('/sb-fn/');
  } catch {
    return false;
  }
}

export function installFetchTracing(): void {
  if (installed || typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  installed = true;

  const original = window.fetch.bind(window);

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const url =
        typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

      if (isBackendCall(url)) {
        // Preserve any caller-supplied headers (from init first, else from a Request input).
        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
        headers.set('X-Request-Id', newRequestId());
        headers.set('X-Session-Id', getSessionId());
        return original(input, { ...init, headers });
      }
    } catch {
      // Never let tracing break a real request — fall through to the untouched call.
    }
    return original(input, init);
  };
}
